import type {
  AttendeeConferenceReviewEvidence,
  AttendeeConferenceReviewValues,
  AttendeeLearningRecordSources,
  AttendeeLearningRecordValues,
  AttendeeLearningStatementReference,
} from "@verity-connect/contracts";
import { simplifyTeacherLanguage } from "@verity-connect/domain";
import acceptedDemo from "./learning-companion-acceptance-demo.json";

export interface DemoSession {
  id: string;
  day: string;
  time: string;
  title: string;
  presenter: string;
  room: string;
  audience: string;
  description: string;
  materials: readonly { name: string; detail: string }[];
  recording: { duration: string; wordCount: string; speakers: string; excerpt: string };
  notes: { markers: readonly string[]; typed: string; question: string; nextStep: string };
  values: AttendeeLearningRecordValues;
  sources: AttendeeLearningRecordSources;
  updatedAt: string;
}

const sessionDetails = {
  "visible-routines": {
    day: "Thursday, September 17", time: "9:00–9:50 a.m.",
    presenter: "Rachel Morgan", room: "Heritage Hall A",
    description: "A practical session on replacing broad directions with a visible three-part routine that helps students begin and continue work with less teacher prompting.",
    material: "Presenter slides: Cue, Model, Release", handoutWords: "28 slides", transcriptWords: "4,151 words",
    excerpt: "We are trying to create students who can see the next move, try it, judge what happened, and continue with less teacher prompting.",
    typed: "I often say ‘get started’ and then answer five questions about what starting means. I want the first move to be plain without solving the hard part for students.",
    marker: "Make the next step visible before asking students to work independently.",
    question: "How much modeling is enough before release, especially for students who wait for the teacher to solve the hard part?",
    nextStep: "Rewrite Monday's opening direction as one cue, one short model, and one timed release.",
  },
  "actionable-feedback": {
    day: "Thursday, September 17", time: "10:10–11:00 a.m.",
    presenter: "Michael Reyes", room: "Heritage Hall B",
    description: "A working session on shortening feedback and naming one useful next move without hiding the larger learning goal.",
    material: "Presenter slides: Notice, Name, Next", handoutWords: "22 slides", transcriptWords: "3,059 words",
    excerpt: "Feedback is not complete when the teacher sends it. It is complete when the student interprets it and tries something.",
    typed: "I give accurate feedback, but it often arrives as a paragraph. The useful part gets buried. I need one immediate step to stay tied to the larger goal.",
    marker: "Make the next step visible while keeping it tied to a stable learning goal.",
    question: "How can one next step stay manageable without hiding a larger pattern the student also needs to address?",
    nextStep: "Rewrite one planned comment as Notice, Name, Next and ask a colleague to identify the student's next action.",
  },
  "thinking-questions": {
    day: "Thursday, September 17", time: "11:20 a.m.–12:10 p.m.",
    presenter: "Jenna Caldwell", room: "Cedar Room",
    description: "A practical workshop on asking one focused question, protecting wait time, sampling several responses, and choosing a follow-up from what students reveal.",
    material: "Presenter slides: Ask, Wait, Sample, Follow", handoutWords: "31 slides", transcriptWords: "2,284 words",
    excerpt: "A correct first answer is not a class sample. Wait time works when students know what they are preparing.",
    typed: "I often call on the first hand, hear the answer I hoped for, and move on. That tells me somebody knows, not what the class is thinking.",
    marker: "Plan how every student will prepare and how I will sample before I ask the question.",
    question: "How can I preserve a student's partial idea long enough to examine it without making the student feel exposed?",
    nextStep: "Rewrite Tuesday's opening question as one Ask, Wait, Sample, Follow cycle with a thirty-second private jot.",
  },
  "meaningful-reading": {
    day: "Friday, September 18", time: "9:00–9:50 a.m.",
    presenter: "Daniel Brooks", room: "Chapel Classroom",
    description: "A cross-content session on helping students notice an important detail, name what it shows, and link it to the larger question without turning reading into worksheet completion.",
    material: "Presenter handout: Notice, Name, Link", handoutWords: "412 words", transcriptWords: "1,944 words",
    excerpt: "A detail matters because of the idea it supports, not because it appears on a worksheet.",
    typed: "I use too many questions during reading because I am afraid students will miss something. They hunt for answers and lose the larger idea.",
    marker: "A reading prompt should help students select evidence and connect it to a purpose, not reward completion of many questions.",
    question: "How do I choose stopping places that support readers without breaking the flow of meaning?",
    nextStep: "Cut Thursday's reading guide to one guiding question and two Notice, Name, Link stopping places.",
  },
  "calm-corrections": {
    day: "Friday, September 18", time: "10:10–11:00 a.m.",
    presenter: "Alicia Grant", room: "Heritage Hall C",
    description: "A classroom-practice session on responding to low-level disruption with a brief pause, a neutral description, a real choice, and a clear path back into the work.",
    material: "Presenter handout: Pause, Name, Choice, Return", handoutWords: "373 words", transcriptWords: "1,989 words",
    excerpt: "A calm correction is not softness, and a firm boundary does not require public force.",
    typed: "My corrections get longer when I feel ignored. Then the whole class watches, and returning becomes harder for everyone.",
    marker: "Make the boundary and path back clear with as few public words as possible.",
    question: "When is offering a choice helpful, and when does it make a simple direction sound negotiable?",
    nextStep: "Rehearse one Pause, Name, Choice, Return response for side conversations during student explanations.",
  },
  "planning-transfer": {
    day: "Friday, September 18", time: "11:20 a.m.–12:10 p.m.",
    presenter: "Samuel Price", room: "Cedar Room",
    description: "A planning workshop on helping students recognize when a learned move applies, adapt it to a changed task, and use it without the original teacher prompt.",
    material: "Presenter handout: Same, Shift, Solo", handoutWords: "385 words", transcriptWords: "2,049 words",
    excerpt: "Performing a strategy after the teacher names it is not the same as choosing it.",
    typed: "I think students have learned a strategy because they complete the practice page that names it. The missing part may be knowing when or why to choose it.",
    marker: "Transfer includes recognizing when a learned move fits, adapting it, and knowing when another move is better.",
    question: "How quickly can I remove the strategy label without confusing students who still need a cue?",
    nextStep: "Add one changed case, one non-example, and one unlabeled Solo task to next week's evidence lesson.",
  },
} as const;

export function simplifyDemoText(text: string): string {
  return simplifyTeacherLanguage(text);
}

function simplifyDemoValue<T>(value: T): T {
  if (typeof value === "string") return simplifyDemoText(value) as T;
  if (Array.isArray(value)) return value.map(simplifyDemoValue) as T;
  if (value && typeof value === "object") return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, simplifyDemoValue(entry)]),
  ) as T;
  return value;
}

export const demoSessions: readonly DemoSession[] = acceptedDemo.sessions.map((accepted, index) => {
  const detail = sessionDetails[accepted.itemId as keyof typeof sessionDetails];
  if (!detail) throw new Error(`Missing demo detail for ${accepted.itemId}.`);
  const values = simplifyDemoValue(accepted.learningRecord) as AttendeeLearningRecordValues;
  return {
    id: accepted.itemId,
    day: detail.day,
    time: detail.time,
    title: accepted.title,
    presenter: detail.presenter,
    room: detail.room,
    audience: "K–12 educators",
    description: detail.description,
    materials: [{
      name: detail.material,
      detail: index < 3
        ? `${detail.handoutWords} · PowerPoint · PDF prepared automatically`
        : `${detail.handoutWords} · presenter handout`,
    }],
    recording: { duration: "50:00", wordCount: detail.transcriptWords, speakers: "Presenter and participant questions", excerpt: detail.excerpt },
    notes: { markers: [detail.marker], typed: detail.typed, question: detail.question, nextStep: detail.nextStep },
    values,
    sources: learningSources(accepted.itemId, simplifyDemoValue(accepted.sourceReferences) as readonly AttendeeLearningStatementReference[]),
    updatedAt: `2026-09-${index < 3 ? "17" : "18"}T${index % 3 === 0 ? "10:02" : index % 3 === 1 ? "11:12" : "12:22"}:00-04:00`,
  };
});

export const demoConferenceEvidence: AttendeeConferenceReviewEvidence = {
  eventId: "sample-event",
  coverage: acceptedDemo.conferenceReview.coverage as AttendeeConferenceReviewEvidence["coverage"],
  sources: demoSessions.map((session) => ({
    itemId: session.id,
    title: session.title,
    learningRecordRevision: 1,
    officialDescriptionIncluded: true,
    presenterResourceIds: [`presenter:${session.id}:handout`],
  })),
  builtAt: "2026-08-11T16:00:00.000Z",
};

export const demoConferenceValues = simplifyDemoValue(acceptedDemo.conferenceReview.values) as unknown as AttendeeConferenceReviewValues;

function learningSources(itemId: string, statements: readonly AttendeeLearningStatementReference[]): AttendeeLearningRecordSources {
  return {
    noteRevision: 1,
    officialDescriptionIncluded: true,
    presenterResourceIds: [`presenter:${itemId}:handout`],
    statements: statements.map((statement) => ({ ...statement, sourceIds: [...statement.sourceIds] })),
    capturedAt: "2026-08-11T16:00:00.000Z",
  };
}
